import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { api, gbp } from "../lib/api";
import { CONTACT } from "../lib/content";
import { FadeUp } from "../components/Reveal";
import Triskelion from "../components/Triskelion";

export function OrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const isEnquiry = params.get("enquiry") === "1";
  const [status, setStatus] = useState(isEnquiry ? "enquiry" : "checking");
  const [orderNumber, setOrderNumber] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("cg_last_order"))?.order_number || "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setOrderNumber(data.order_number || orderNumber);
        if (data.payment_status === "paid") return setStatus("paid");
        if (data.status === "expired" || data.status === "failed") return setStatus("failed");
        if (++attempts < 20) setTimeout(poll, 2000);
        else setStatus("pending");
      } catch {
        if (++attempts < 20) setTimeout(poll, 2000);
        else setStatus("pending");
      }
    };
    poll();
  }, [sessionId]);

  return (
    <main className="pt-48 pb-32" data-testid="order-success">
      <div className="max-w-xl mx-auto px-6 text-center">
        <FadeUp>
          <Triskelion className="w-12 h-12 text-gold mx-auto mb-8" strokeWidth={8} />
          {status === "checking" && (
            <>
              <h1 className="font-serif text-4xl text-ivory font-light mb-4">Confirming your payment...</h1>
              <p className="text-stone-mist font-light">This takes just a moment.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle2 className="w-10 h-10 text-gold mx-auto mb-6" />
              <h1 className="font-serif text-4xl text-ivory font-light mb-4">Thank You for Your Order</h1>
              {orderNumber && <p className="font-mono text-gold tracking-widest mb-6" data-testid="order-number">{orderNumber}</p>}
              <p className="text-stone-mist font-light leading-relaxed">
                Your payment has been received. Celtic Gold will confirm acceptance of your order by email. If you
                have any questions in the meantime, call us on {CONTACT.phoneShort} or email {CONTACT.email}.
              </p>
            </>
          )}
          {status === "enquiry" && (
            <>
              <CheckCircle2 className="w-10 h-10 text-gold mx-auto mb-6" />
              <h1 className="font-serif text-4xl text-ivory font-light mb-4">Thank You for Your Enquiry</h1>
              {orderNumber && <p className="font-mono text-gold tracking-widest mb-6" data-testid="order-number">{orderNumber}</p>}
              <p className="text-stone-mist font-light leading-relaxed">
                We have received your order enquiry. Celtic Gold will confirm availability and acceptance by email —
                nothing is charged until then. Questions? Call {CONTACT.phoneShort}.
              </p>
            </>
          )}
          {(status === "pending" || status === "failed") && (
            <>
              <h1 className="font-serif text-4xl text-ivory font-light mb-4">
                {status === "pending" ? "Payment pending" : "Payment not completed"}
              </h1>
              <p className="text-stone-mist font-light leading-relaxed">
                {status === "pending"
                  ? "We're still confirming your payment — please check back shortly or contact us."
                  : "Your payment did not complete. No charge has been made — please try again or place an order enquiry instead."}
              </p>
            </>
          )}
          <Link to="/shop/all" data-testid="success-continue" className="inline-block mt-10 border border-gold/40 text-gold px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-gold hover:text-ink transition-colors duration-300">
            Continue Shopping
          </Link>
        </FadeUp>
      </div>
    </main>
  );
}

export function OrderCancel() {
  return (
    <main className="pt-48 pb-32" data-testid="order-cancel">
      <div className="max-w-xl mx-auto px-6 text-center">
        <FadeUp>
          <XCircle className="w-10 h-10 text-stone-mist mx-auto mb-6" />
          <h1 className="font-serif text-4xl text-ivory font-light mb-4">Checkout cancelled</h1>
          <p className="text-stone-mist font-light mb-10">No payment was taken. Your basket has been kept if you'd like to try again.</p>
          <Link to="/checkout" data-testid="cancel-retry" className="inline-block bg-gold text-ink px-8 py-4 text-xs uppercase tracking-[0.25em] font-medium hover:bg-gold-glow transition-colors">
            Return to Checkout
          </Link>
        </FadeUp>
      </div>
    </main>
  );
}
